import { useEffect, useState } from "react";
import { portalApi, INR, Page } from "./PortalShell";
import { UserPlus, Calendar, X } from "lucide-react";

export default function PortalOrdersPage() {
  const [data, setData] = useState({ orders: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [newOnly, setNewOnly] = useState(false);
  const [thisMonth, setThisMonth] = useState(false);
  const LIMIT = 50;

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: LIMIT, offset: page * LIMIT });
    if (newOnly) params.set("new_only", "true");
    if (thisMonth) params.set("month", "this");
    portalApi.get(`/affiliate/orders?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [page, newOnly, thisMonth]);

  // Reset paging whenever filters flip
  useEffect(() => { setPage(0); }, [newOnly, thisMonth]);

  const totalPages = Math.ceil((data.total || 0) / LIMIT);
  const hasFilters = newOnly || thisMonth;

  return (
    <Page title="Orders" subtitle={`${data.total} order${data.total === 1 ? "" : "s"} ${hasFilters ? "matching your filters" : "driven by your codes"}`} testId="portal-orders-page">
      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="orders-filters">
        <Chip
          testId="filter-new-only"
          active={newOnly}
          onClick={() => setNewOnly((v) => !v)}
          icon={UserPlus}
        >
          New customers only
        </Chip>
        <Chip
          testId="filter-this-month"
          active={thisMonth}
          onClick={() => setThisMonth((v) => !v)}
          icon={Calendar}
        >
          This month
        </Chip>
        {hasFilters && (
          <button
            onClick={() => { setNewOnly(false); setThisMonth(false); }}
            data-testid="filter-clear"
            className="ml-1 inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-200"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>
      {loading ? (
        <div className="text-neutral-500 italic py-8">Loading…</div>
      ) : data.orders.length === 0 ? (
        <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-8 text-center text-neutral-500 text-sm">
          {hasFilters
            ? "No orders match these filters yet. Try clearing them or picking a different slice."
            : "No orders yet — share your code to start driving sales."}
        </div>
      ) : (
        <>
          {/* Mobile: stacked cards (every field visible) */}
          <div className="md:hidden space-y-2">
            {data.orders.map((o, i) => (
              <div
                key={i}
                data-testid={`order-card-${i}`}
                className="rounded-2xl bg-neutral-900 border border-neutral-800 p-3.5"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Order</div>
                    <div className="text-sm font-mono truncate">
                      {o.shopify_number || o.order_ref?.slice(0, 8) || "—"}
                      {o.is_new_customer && (
                        <span className="ml-1 text-[9px] font-semibold text-orange-400 uppercase">New</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Amount</div>
                    <div className="text-sm font-semibold tabular-nums">{INR(o.amount)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-2 border-t border-neutral-800/60">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Customer</div>
                    <div className="text-xs truncate text-neutral-200">
                      {o.customer_name || o.customer_email || "—"}
                    </div>
                  </div>
                  <div className="min-w-0 text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Commission</div>
                    <div className="text-xs text-emerald-400 font-semibold tabular-nums">
                      {INR(o.commission_amount)}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Code</div>
                    <div className="text-xs font-mono text-neutral-300 truncate">
                      {o.discount_code || (o.attribution_source === "tag" ? "via link" : "—")}
                    </div>
                  </div>
                  <div className="min-w-0 text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Date</div>
                    <div className="text-xs text-neutral-400 tabular-nums">
                      {o.order_date?.slice(0, 10)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop / tablet: full table */}
          <div className="hidden md:block rounded-2xl bg-neutral-900 border border-neutral-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900/60 border-b border-neutral-800">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Order</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold hidden lg:table-cell">Code</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Amount</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Comm.</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((o, i) => (
                  <tr key={i} data-testid={`order-row-${i}`} className="border-b border-neutral-800/60 last:border-0">
                    <td className="px-4 py-2.5 text-xs font-mono">
                      {o.shopify_number || o.order_ref?.slice(0, 8) || "—"}
                      {o.is_new_customer && <span className="ml-1 text-[9px] font-semibold text-orange-400 uppercase">New</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs truncate max-w-[180px]">{o.customer_name || o.customer_email || "—"}</td>
                    <td className="px-4 py-2.5 text-xs font-mono text-neutral-400 hidden lg:table-cell">{o.discount_code || (o.attribution_source === "tag" ? "via link" : "—")}</td>
                    <td className="px-4 py-2.5 text-xs text-right font-semibold">{INR(o.amount)}</td>
                    <td className="px-4 py-2.5 text-xs text-right text-emerald-400">{INR(o.commission_amount)}</td>
                    <td className="px-4 py-2.5 text-xs text-right text-neutral-500">{o.order_date?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <div>Page {page + 1} of {totalPages}</div>
              <div className="flex gap-2">
                <button
                  data-testid="orders-prev"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded-full bg-neutral-900 border border-neutral-800 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  data-testid="orders-next"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 rounded-full bg-neutral-900 border border-neutral-800 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Page>
  );
}

function Chip({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "bg-orange-500 border-orange-400 text-white"
          : "bg-neutral-900 border-neutral-800 text-neutral-300 hover:border-orange-500/50 hover:text-neutral-100"
      }`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />} {children}
    </button>
  );
}
